import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaCheck, FaEye, FaEyeSlash, FaTimes } from 'react-icons/fa';
import { FiArrowRight, FiCheckCircle, FiMail, FiStar } from 'react-icons/fi';
import api from '../axios';
import { useAuth } from '../context/AuthContext';

const trustNotes = [
  'Registration creates your account and starts the trial setup process.',
  'Email verification is required before your first login.',
  'After verification, the next step is MT5 connection and configuration.',
];

function Register() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [tradingExperience, setTradingExperience] = useState('');
  const [primaryMarket, setPrimaryMarket] = useState('');
  const [company, setCompany] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const navigate = useNavigate();
  const { setAuth } = useAuth();

  const checkPasswordStrength = (pass) => {
    setPasswordStrength({
      length: pass.length >= 6,
      uppercase: /[A-Z]/.test(pass),
      lowercase: /[a-z]/.test(pass),
      number: /\d/.test(pass),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(pass),
    });
  };

  const isPasswordValid = () => Object.values(passwordStrength).every((requirement) => requirement === true);

  useEffect(() => {
    if (password) {
      checkPasswordStrength(password);
    } else {
      setPasswordStrength({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
      });
    }
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    if (!firstName || !lastName || !email || !phone || !country || !tradingExperience || !primaryMarket || !password || !confirmPassword) {
      setError('Please complete the full registration form.');
      setIsLoading(false);
      return;
    }

    if (!isPasswordValid()) {
      setError('Please ensure your password meets all requirements.');
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.post('/api/register', {
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
        country,
        trading_experience: tradingExperience,
        primary_market: primaryMarket,
        company,
        password,
      }, {
        timeout: 45000,
      });

      localStorage.removeItem('token');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userId');
      localStorage.removeItem('isLoggedIn');
      document.cookie = 'access_token=; Max-Age=0; path=/;';
      setAuth({ isAuthed: false, isLoading: false, user: null, subscriptionStatus: null });

      setSuccess(response.data?.message || 'Account created successfully. Your verification email is being sent now. Verify your account, then log in.');
      setTimeout(() => {
        navigate('/login');
      }, 2200);
    } catch (err) {
      let errorMessage = 'Failed to create account';

      if (err.response) {
        if (err.response.status === 400 && err.response.data?.detail?.message) {
          errorMessage = err.response.data.detail.message;
        } else if (err.response.status === 400) {
          errorMessage = err.response.data?.detail || 'Invalid request. Please check your input.';
        } else if (err.response.status === 409) {
          errorMessage = 'Email already registered. Please log in or use a different email.';
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const completedRequirements = Object.values(passwordStrength).filter(Boolean).length;
  const experienceOptions = ['Beginner', 'Intermediate', 'Advanced', 'Professional'];
  const marketOptions = ['Forex', 'Crypto', 'Indices', 'Commodities', 'Multi-asset'];

  return (
    <div className="min-h-screen overflow-hidden bg-[#07111a] text-white">
      <div className="fixed inset-0 -z-20 bg-[radial-gradient(circle_at_top_left,_rgba(27,186,123,0.16),_transparent_28%),radial-gradient(circle_at_80%_20%,_rgba(45,122,255,0.16),_transparent_30%),linear-gradient(180deg,_#09131d_0%,_#05080c_100%)]" />

      <div className="mx-auto grid min-h-screen max-w-7xl gap-10 px-4 pb-12 pt-28 sm:px-6 lg:grid-cols-[1fr_1fr] lg:items-center lg:px-8 lg:pt-32">
        <section>
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-4 py-2 text-sm font-semibold text-emerald-200">
            <FiStar className="h-4 w-4" />
            Create your account
          </div>

          <h1 className="mt-6 max-w-3xl text-5xl font-black leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
            Create your account,
            <span className="block bg-gradient-to-r from-emerald-300 via-cyan-300 to-sky-400 bg-clip-text text-transparent">
              then continue to setup
            </span>
          </h1>

          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
            Create a complete account profile, verify your email, then sign in and continue with MT5 setup.
          </p>

          <div className="mt-10 space-y-4">
            {trustNotes.map((note) => (
              <div key={note} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 text-slate-300 backdrop-blur-xl">
                <FiCheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-300" />
                <span>{note}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-[2rem] border border-white/10 bg-white/5 p-6 shadow-[0_30px_80px_rgba(0,0,0,0.35)] backdrop-blur-xl sm:p-8">
          <div className="border-b border-white/10 pb-5">
            <p className="text-sm uppercase tracking-[0.24em] text-cyan-200/70">Account creation</p>
            <h2 className="mt-2 text-3xl font-black text-white">Create your account</h2>
          </div>

          {error && (
            <div className="mt-6 rounded-2xl border border-rose-300/20 bg-rose-300/10 p-4 text-sm text-rose-200">
              {error}
            </div>
          )}

          {success && (
            <div className="mt-6 rounded-2xl border border-emerald-300/20 bg-emerald-300/10 p-4 text-sm text-emerald-200">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="register-first-name" className="mb-2 block text-sm font-medium text-slate-300">
                  First name
                </label>
                <input
                  type="text"
                  id="register-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/65 px-4 py-3 text-white outline-none transition focus:border-cyan-300/40"
                  required
                />
              </div>

              <div>
                <label htmlFor="register-last-name" className="mb-2 block text-sm font-medium text-slate-300">
                  Last name
                </label>
                <input
                  type="text"
                  id="register-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/65 px-4 py-3 text-white outline-none transition focus:border-cyan-300/40"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="register-email" className="mb-2 block text-sm font-medium text-slate-300">
                Email
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
                  <FiMail className="h-4 w-4" />
                </div>
                <input
                  type="email"
                  id="register-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/65 py-3 pl-11 pr-4 text-white outline-none transition focus:border-cyan-300/40"
                  required
                />
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="register-phone" className="mb-2 block text-sm font-medium text-slate-300">
                  Phone number
                </label>
                <input
                  type="tel"
                  id="register-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/65 px-4 py-3 text-white outline-none transition focus:border-cyan-300/40"
                  required
                />
              </div>

              <div>
                <label htmlFor="register-country" className="mb-2 block text-sm font-medium text-slate-300">
                  Country
                </label>
                <input
                  type="text"
                  id="register-country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/65 px-4 py-3 text-white outline-none transition focus:border-cyan-300/40"
                  required
                />
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="register-experience" className="mb-2 block text-sm font-medium text-slate-300">
                  Trading experience
                </label>
                <select
                  id="register-experience"
                  value={tradingExperience}
                  onChange={(e) => setTradingExperience(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/65 px-4 py-3 text-white outline-none transition focus:border-cyan-300/40"
                  required
                >
                  <option value="">Select experience</option>
                  {experienceOptions.map((option) => (
                    <option key={option} value={option.toLowerCase()}>{option}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="register-market" className="mb-2 block text-sm font-medium text-slate-300">
                  Primary market
                </label>
                <select
                  id="register-market"
                  value={primaryMarket}
                  onChange={(e) => setPrimaryMarket(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/65 px-4 py-3 text-white outline-none transition focus:border-cyan-300/40"
                  required
                >
                  <option value="">Select market</option>
                  {marketOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="register-company" className="mb-2 block text-sm font-medium text-slate-300">
                Company or trading desk (optional)
              </label>
              <input
                type="text"
                id="register-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="w-full rounded-2xl border border-white/10 bg-slate-950/65 px-4 py-3 text-white outline-none transition focus:border-cyan-300/40"
              />
            </div>

            <div>
              <label htmlFor="register-password" className="mb-2 block text-sm font-medium text-slate-300">
                Password
              </label>
              <div className="relative mb-3">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="register-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setIsPasswordFocused(true)}
                  onBlur={() => setIsPasswordFocused(false)}
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/65 py-3 pl-4 pr-12 text-white outline-none transition focus:border-cyan-300/40"
                  required
                  aria-describedby="password-requirements"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 transition hover:text-white"
                  tabIndex="-1"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              <div
                id="password-requirements"
                className={`overflow-hidden rounded-2xl border border-white/10 bg-slate-950/60 p-4 text-sm transition-all duration-300 ${isPasswordFocused || password ? 'max-h-72 opacity-100' : 'max-h-0 border-transparent p-0 opacity-0'}`}
              >
                <p className="font-medium text-white">Password Requirements</p>
                <ul className="mt-3 space-y-2">
                  <li className="flex items-center">
                    <span className={`mr-2 inline-flex h-4 w-4 items-center justify-center rounded-full ${passwordStrength.length ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                      {passwordStrength.length ? <FaCheck size={10} /> : <FaTimes size={10} className="text-white" />}
                    </span>
                    <span className={passwordStrength.length ? 'text-emerald-300' : 'text-slate-400'}>At least 6 characters</span>
                  </li>
                  <li className="flex items-center">
                    <span className={`mr-2 inline-flex h-4 w-4 items-center justify-center rounded-full ${passwordStrength.uppercase ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                      {passwordStrength.uppercase ? <FaCheck size={10} /> : <FaTimes size={10} className="text-white" />}
                    </span>
                    <span className={passwordStrength.uppercase ? 'text-emerald-300' : 'text-slate-400'}>At least one uppercase letter</span>
                  </li>
                  <li className="flex items-center">
                    <span className={`mr-2 inline-flex h-4 w-4 items-center justify-center rounded-full ${passwordStrength.lowercase ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                      {passwordStrength.lowercase ? <FaCheck size={10} /> : <FaTimes size={10} className="text-white" />}
                    </span>
                    <span className={passwordStrength.lowercase ? 'text-emerald-300' : 'text-slate-400'}>At least one lowercase letter</span>
                  </li>
                  <li className="flex items-center">
                    <span className={`mr-2 inline-flex h-4 w-4 items-center justify-center rounded-full ${passwordStrength.number ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                      {passwordStrength.number ? <FaCheck size={10} /> : <FaTimes size={10} className="text-white" />}
                    </span>
                    <span className={passwordStrength.number ? 'text-emerald-300' : 'text-slate-400'}>At least one number</span>
                  </li>
                  <li className="flex items-center">
                    <span className={`mr-2 inline-flex h-4 w-4 items-center justify-center rounded-full ${passwordStrength.special ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                      {passwordStrength.special ? <FaCheck size={10} /> : <FaTimes size={10} className="text-white" />}
                    </span>
                    <span className={passwordStrength.special ? 'text-emerald-300' : 'text-slate-400'}>At least one special character</span>
                  </li>
                </ul>

                <div className="mt-4 border-t border-white/10 pt-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">Strength</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-800">
                      <div
                        className={`h-full transition-all duration-500 ${isPasswordValid() ? 'bg-emerald-400' : completedRequirements >= 3 ? 'bg-yellow-400' : 'bg-rose-400'}`}
                        style={{ width: `${(completedRequirements / 5) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-slate-300">
                      {isPasswordValid() ? 'Strong' : completedRequirements >= 3 ? 'Medium' : 'Weak'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="register-confirm-password" className="mb-2 block text-sm font-medium text-slate-300">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="register-confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/65 py-3 pl-4 pr-12 text-white outline-none transition focus:border-cyan-300/40"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 transition hover:text-white"
                  tabIndex="-1"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-300 to-cyan-300 px-6 py-4 font-bold text-slate-950 transition hover:scale-[1.01] disabled:opacity-70"
            >
              {isLoading ? 'Creating account...' : 'Create account'}
              {!isLoading && <FiArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <div className="relative mt-8 mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-[#101a24] px-4 text-slate-400">Already registered?</span>
            </div>
          </div>

          <Link
            to="/login"
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/12 bg-white/5 px-6 py-4 font-semibold text-white transition hover:bg-white/10"
          >
            Log in
          </Link>
        </section>
      </div>
    </div>
  );
}

export default Register;
